import { Button, DatePicker, Input, Select } from "antd";
import { Moment } from "moment";
import { translate } from "utils";
import { UndoOutlined } from "@ant-design/icons";
import {
	merchantRequestQueryDefault,
	setMerchantRequestFilter,
} from "store/filter/index.slice";
import { Merchant } from "features/merchant/types";
import { MerchantRequestQueryParams } from "./type";
import { useDispatch } from "react-redux";
import { merchantRequestStatus, merchantRequestType } from "./constant";
import SelectInfinity from "components/select-infinity/SelectInfinity";
import { getMerchantsAPI } from "service/apis";
import moment from "moment";
import { MerchantQueryFilter } from "./index";

const { Option } = Select;
const { RangePicker } = DatePicker;

export interface SearchProps {
	query: MerchantRequestQueryParams;
	setQuery: React.Dispatch<React.SetStateAction<MerchantRequestQueryParams>>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	queryMerchant: MerchantQueryFilter;
	setQueryMerchant: React.Dispatch<React.SetStateAction<MerchantQueryFilter>>;
	merchantList: Merchant[];
	setMerchantList: React.Dispatch<React.SetStateAction<Merchant[]>>;
	getListMerchantRequest: (query: MerchantRequestQueryParams) => void;
}

const MerchantRequestSearch = ({
	query,
	setQuery,
	setLoading,
	merchantList,
	setMerchantList,
	queryMerchant,
	setQueryMerchant,
	getListMerchantRequest,
}: SearchProps) => {
	const dispatch = useDispatch();

	const handleChangeSearchName = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newQuery = {
			...query,
			id: e.target.value,
		};
		setQuery(newQuery);
		dispatch(setMerchantRequestFilter(newQuery));
	};

	const handleSearch = () => {
		const newQuery = {
			...query,
			skip: 0,
		};
		getListMerchantRequest(newQuery);
	};

	const handleChangeRangeDate = (
		dateMoment: [Moment | null, Moment | null] | null,
		dateString: [string, string] | string,
	) => {
		setLoading(true);
		const startDateMoment = dateMoment?.[0]?.startOf("day")?.toISOString();
		const endDateMoment = dateMoment?.[1]?.endOf("day")?.toISOString();

		const newQuery = {
			...query,
			startDate: startDateMoment,
			endDate: endDateMoment,
			skip: 0,
		};

		getListMerchantRequest(newQuery);
	};

	const handleSearchMerchant = (searchValue: string) => {
		setQueryMerchant((prev: MerchantQueryFilter) => {
			if (searchValue) {
				return {
					...prev,
					skip: 0,
					fullName: searchValue,
				};
			} else {
				const prevState = prev;
				delete prevState.fullName;
				return {
					...prevState,
					skip: 0,
				};
			}
		});
	};

	const getLabelByType = (
		type: keyof MerchantRequestQueryParams,
		nameValue?: string,
	) => {
		const value = query[type];
		if (!value) return translate("All");
		if (typeof value === "string") {
			return value;
		}
		return nameValue;
	};

	const handleUpdateQuery = (key: string, value: string) => {
		const newQuery = {
			...query,
			[key]: value,
			skip: 0,
		};
		getListMerchantRequest(newQuery);
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
			<div>
				<p>{translate("Request ID")}</p>
				<Input.Search
					placeholder={translate("Input Request ID")}
					value={query.id || ""}
					onChange={handleChangeSearchName}
					onSearch={handleSearch}
					onBlur={e => {
						setQuery({
							...query,
							id: e.target.value.trim() || null,
						});
					}}
				/>
			</div>

			<div>
				<p>{translate("Merchant")}</p>
				<SelectInfinity
					query={queryMerchant}
					setQuery={setQueryMerchant}
					renderList={merchantList}
					setRenderList={setMerchantList}
					fetchMoreFunc={getMerchantsAPI}
					onSearch={handleSearchMerchant}
					onChange={e => {
						handleUpdateQuery("merchant", e);
					}}
					value={getLabelByType("merchant", query?.merchantName)}
					placeholder={translate("Merchant")}
					className={"w-full"}
					showSearch={true}
					haveAllValue={true}
					optionRender={(item: Merchant, index: number) => (
						<Option value={`${item.fullName} ${item.username}`} key={index}>
							<p>{item.fullName}</p>
							<span className="text-sm text-[#95a5a6]">{item.username}</span>
						</Option>
					)}
				/>
			</div>
			<div>
				<p>{translate("Type")}</p>
				<Select
					placeholder={translate("Select status")}
					className="w-full"
					value={query.type || null}
					onChange={value => handleUpdateQuery("type", value)}
					allowClear
				>
					<Option value={null}>{translate("All")}</Option>
					{merchantRequestType.map(el => (
						<Option key={el.value} value={el.value}>
							<span>{translate(el.label)}</span>
						</Option>
					))}
				</Select>
			</div>

			<div>
				<p>{translate("Request date")}</p>
				<RangePicker
					className="w-full"
					value={[
						query.startDate ? moment(query.startDate) : null,
						query.endDate ? moment(query.endDate) : null,
					]}
					format="YYYY-MM-DD"
					allowEmpty={[true, true]}
					allowClear
					onChange={handleChangeRangeDate}
				/>
			</div>

			<div>
				<p>{translate("Status")}</p>
				<Select
					placeholder={translate("Select status")}
					className="w-full"
					value={query.status || null}
					onChange={value => handleUpdateQuery("status", value)}
					allowClear
				>
					<Option value={null}>{translate("All")}</Option>
					{merchantRequestStatus.map(el => (
						<Option key={el.value} value={el.value}>
							<span>{translate(el.label)}</span>
						</Option>
					))}
				</Select>
			</div>
			<div className="flex justify-between ">
				<Button
					icon={<UndoOutlined />}
					className="flex items-center self-end rounded"
					onClick={() => getListMerchantRequest(merchantRequestQueryDefault)}
				>
					{translate("Reset")}
				</Button>
			</div>
		</div>
	);
};

export default MerchantRequestSearch;
